import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CheckCircle, Mail, Clock, HelpCircle, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SuccessPage() {
  const [sessionId, setSessionId] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [status, setStatus] = useState("processing");

  useEffect(() => {
    // Get session_id from URL if passed
    const urlParams = new URLSearchParams(window.location.search);
    const sid = urlParams.get('session_id');
    const email = urlParams.get('email');
    
    if (sid) setSessionId(sid);
    if (email) setUserEmail(decodeURIComponent(email));
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center p-6">
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#C9A84C]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#0D9488]/10 rounded-full blur-3xl" />
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 max-w-lg w-full"
      >
        {/* Success Card */}
        <div className="glass rounded-3xl p-8 md:p-10 text-center" data-testid="success-card">
          {/* Success Icon */}
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#22C55E]/20 to-[#16A34A]/20 flex items-center justify-center border border-[#22C55E]/30">
            <CheckCircle className="w-10 h-10 text-[#22C55E]" />
          </div>

          {/* Title */}
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-[#E2E8F0] mb-3">
            Payment Successful!
          </h1>
          
          <p className="text-[#C9A84C] text-lg mb-6">
            Your NeuroEnergetic BP Blueprint is being generated
          </p>

          {/* Info Box */}
          <div className="glass-gold rounded-xl p-6 mb-8 text-left">
            <div className="flex items-start gap-4 mb-4">
              <Mail className="w-6 h-6 text-[#C9A84C] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-[#E2E8F0] mb-1">Check Your Email</h3>
                <p className="text-sm text-[#94A3B8]">
                  You will receive your personalized 15+ page PDF blueprint at:
                  {userEmail && (
                    <span className="block mt-1 text-[#C9A84C] font-medium">{userEmail}</span>
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Clock className="w-6 h-6 text-[#C9A84C] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-[#E2E8F0] mb-1">Delivery Time</h3>
                <p className="text-sm text-[#94A3B8]">
                  Your blueprint will be delivered within <strong className="text-[#E2E8F0]">24 hours</strong>. 
                  Most reports are generated within 5-15 minutes.
                </p>
              </div>
            </div>
          </div>

          {/* What Happens Next */}
          <div className="text-left mb-8">
            <h3 className="font-heading text-lg font-semibold text-[#E2E8F0] mb-4">What happens next?</h3>
            <ol className="space-y-3 text-sm text-[#94A3B8]">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#C9A84C]/20 text-[#C9A84C] flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                <span>Our AI system analyzes your birth data, ancestral lineage, and elemental constitution</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#C9A84C]/20 text-[#C9A84C] flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                <span>A premium PDF report is generated with your Five Elements diagnosis</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#C9A84C]/20 text-[#C9A84C] flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                <span>Your blueprint is delivered to your email as an attachment</span>
              </li>
            </ol>
          </div>

          {/* Help Section */}
          <div className="border-t border-white/10 pt-6">
            <div className="flex items-center justify-center gap-2 text-sm text-[#94A3B8] mb-4">
              <HelpCircle className="w-4 h-4" />
              <span>Didn't receive your email?</span>
            </div>
            <ul className="text-xs text-[#64748B] space-y-1 mb-4">
              <li>• Check your spam/junk folder</li>
              <li>• Wait up to 24 hours for delivery</li>
              <li>• Contact us if you still don't receive it</li>
            </ul>
            <a 
              href="mailto:visitamedicaonline@gmail.com?subject=Blueprint%20Delivery%20Issue"
              className="text-[#C9A84C] hover:underline text-sm"
            >
              visitamedicaonline@gmail.com
            </a>
          </div>

          {/* Session ID for reference */}
          {sessionId && (
            <div className="mt-6 pt-4 border-t border-white/10">
              <p className="text-xs text-[#64748B]">
                Reference ID: <span className="font-mono">{sessionId}</span>
              </p>
            </div>
          )}

          {/* Back to Home */}
          <div className="mt-8">
            <a href="/">
              <Button variant="outline" className="text-[#94A3B8] border-white/20 hover:bg-white/5">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to NeuroVitale
              </Button>
            </a>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-[#64748B] mt-6">
          © {new Date().getFullYear()} NeuroVitale™ by Visita Médica Online
        </p>
      </motion.div>
    </div>
  );
}
